/**
 * App entry point.
 * @file src/web/main.tsx
 *
 * Provider order (matches uikit AGENTS.md):
 *   ThemeProvider > ToastProvider (sibling) > ConfirmProvider (wraps)
 *
 * - ThemeProvider owns the theme / dark-mode state.
 * - ToastProvider mounts the Toaster; it's a self-closing sibling
 *   and does NOT wrap children. Call sites use `toast.success(...)`.
 * - ConfirmProvider wraps children because `useConfirm()` reads
 *   context. Admin pages use `confirm.destructive(...)` for
 *   typed-verify deletes.
 *
 * All three must each be mounted exactly once. Adding a duplicate
 * fires a dev-only warning and produces doubled behavior in prod.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfirmProvider, ThemeProvider, ToastProvider } from '@bloomneo/uikit';
import { useDarkMode } from './shared/hooks/useDarkMode';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './styles/index.css';
import './styles/globals.css';
import '@bloomneo/uikit/styles';

/**
 * Single QueryClient for the whole app. Defaults tuned for an admin
 * console:
 *   - staleTime: 30s — cached data re-uses for 30s before marking
 *     stale. Keeps dashboard-to-audit navigation instant without
 *     showing outdated numbers for long.
 *   - refetchOnWindowFocus: true — admins flipping between tabs
 *     get fresh data when they return.
 *   - retry: 1 — retry once on network wobble; don't hammer on real
 *     500s (the admin will see the error toast and can retry).
 *
 * TODO: Expose the QueryClient via a hook if you need programmatic
 * cache invalidation from outside React (e.g. after a websocket push).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Register the service worker on app boot — needed for Web Push and
// for the PWA install banner (Android Chrome) / "Add to Home Screen"
// install behavior on iOS Safari. SW is intentionally minimal: it
// only handles `push` and `notificationclick` events. No precaching
// yet (would need cache-busting on every deploy).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[sw] register failed', err);
    });
  });
}

/**
 * Keeps uikit's ThemeProvider in step with our own light/dark preference.
 *
 * ThemeProvider needs `mode` as a prop, but the preference lives in the
 * useDarkMode store (shared with the rail's toggle) — so it has to be read
 * inside a component. forceConfig stays true: the app pins the "fresherbot"
 * theme and only the mode varies.
 */
function Themed({ children }: { children: React.ReactNode }) {
  const { resolved } = useDarkMode();
  return (
    <ThemeProvider theme="fresherbot" mode={resolved} forceConfig={true}>
      {children}
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Themed>
      <QueryClientProvider client={queryClient}>
        {/* position="top-right" + richColors so stacked toasts don't
            smear. See uikit AGENTS.md for the provider-tree rule. */}
        <ToastProvider position="top-right" richColors />
        <ConfirmProvider>
          <App />
        </ConfirmProvider>
      </QueryClientProvider>
    </Themed>
  </React.StrictMode>
);
